import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, Check, Save, User } from 'lucide-react';
import { customerApi } from '@/features/customers/api/customerApi';
import ChangePasswordForm from '@/components/ChangePasswordForm';
import LocationPicker from '@/features/locations/components/LocationPicker';
import '../customer.css';

export default function UserProfile() {
  const location = useLocation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(null);
  const [state, setState] = useState('loading');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const data = await customerApi.getProfile();
        setProfile(data);
        setForm({
          first_name: data.first_name ?? '',
          last_name: data.last_name ?? '',
          email: data.email ?? '',
          address: data.address ?? '',
          postal_code: data.postal_code ?? '',
          profile_picture: data.profile_picture ?? '',
          location_id: data.location_id ?? null,
          latitude: data.latitude ?? null,
          longitude: data.longitude ?? null,
        });
        setState('ready');
      } catch (requestError) {
        setError(requestError.message);
        setState('error');
      } finally {
        setState((current) => current === 'loading' ? 'error' : current);
      }
    };

    loadProfile();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setSuccess('');
  };

  const handleLocationPatch = (patch) => {
    setForm((current) => ({ ...current, ...patch }));
    setSuccess('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const updated = await customerApi.updateProfile(form);
      setProfile(updated);
      setForm((current) => ({ ...current, ...updated }));
      setSuccess('اطلاعات پروفایل با موفقیت ذخیره شد.');
      if (location.state?.from === '/checkout') navigate('/checkout');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  if (state === 'loading') return <div className="customer-profile-page">در حال دریافت اطلاعات پروفایل...</div>;
  if (state === 'error') return <div className="customer-profile-page"><div className="customer-profile-alert"><AlertCircle size={16} /> {error}</div></div>;

  const fullName =
      [profile.first_name, profile.last_name]
          .filter(Boolean)
          .join(' ') || '—';

  return (
      <div className="customer-profile-page">
        <div className="customer-profile-header">
          <div>
            <h1 className="customer-profile-title">پروفایل مشتری</h1>
            <p className="customer-profile-subtitle">مدیریت اطلاعات حساب کاربری {fullName}</p>
          </div>
          <div className="customer-profile-avatar" aria-hidden="true"><User size={24} /></div>
        </div>

        {/* === [REMOVED] ویجت معرفی از اینجا حذف شد و به کیف پول منتقل شد === */}

        {error && <div className="customer-profile-alert"><AlertCircle size={16} /> {error}</div>}
        {success && <div className="customer-profile-success"><Check size={16} /> {success}</div>}

        <form className="card customer-profile-form" onSubmit={handleSubmit}>
          <section className="customer-profile-section">
            <h2><User size={17} /> اطلاعات شخصی</h2>
            <div className="customer-profile-grid">
              <label>نام<input name="first_name" value={form.first_name} onChange={handleChange} required /></label>
              <label>نام خانوادگی<input name="last_name" value={form.last_name} onChange={handleChange} required /></label>
              <label>ایمیل<input name="email" type="email" value={form.email} onChange={handleChange} /></label>
              <label>شماره موبایل<input value={profile.mobile_phone || ''} disabled /></label>
              <label>کد ملی<input value={profile.national_id || ''} disabled /></label>
              <label>کد پستی<input name="postal_code" value={form.postal_code} onChange={handleChange} required /></label>
            </div>
            <label className="customer-profile-wide">آدرس<textarea name="address" value={form.address} onChange={handleChange} required /></label>

            <LocationPicker
                value={{
                  location_id: form.location_id,
                  latitude: form.latitude,
                  longitude: form.longitude,
                  address: form.address,
                }}
                onChange={handleLocationPatch}
            />
          </section>

          <div className="customer-profile-actions">
            <button className="customer-profile-save" type="submit" disabled={saving}>
              <Save size={16} /> {saving ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
            </button>
            <Link className="customer-profile-cancel" to="/products">بازگشت به محصولات</Link>
          </div>
        </form>
        <ChangePasswordForm />
      </div>
  );
}